/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Switch, Text, TextInput, View } from 'react-native';
import { getProductById } from '../../catalog/infra/productsRepo';
import { searchProducts } from '../../catalog/infra/catalogReadRepo';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { CatalogStockProductSearchFields } from '../../shared/product-search/components/CatalogStockProductSearchFields';
import { NumericTextInput } from '../../shared/ui/NumericTextInput';
import { ModalShell } from '../../shared/modals/ModalShell';
import { ModalFormField } from '../../shared/modals/ModalFormField';
import { ModalFormSection } from '../../shared/modals/ModalFormSection';
import { OperationalConfirmModal } from '../../shared/modals/OperationalConfirmModal';
import { TicketButton } from '../../shared/ticket/TicketButton';
import { ticketControls } from '../../shared/ticket/ticketControls.styles';
import { ticketColors } from '../../shared/theme/ticketColors';
import type { PriceListPricingMode, PriceListUpsertInput, PriceListWithItems } from '../domain/priceListTypes';

type DraftItem = {
  id?: string;
  productId: string;
  /** Nombre resuelto del producto; vacío mientras se resuelve (no mostrar el id crudo). */
  productName: string;
  price: string;
  percentOff: string;
  minQty: string;
};

type Props = {
  visible: boolean;
  initial: PriceListWithItems | null;
  /** Solo lectura (sin permiso de edición): campos deshabilitados, sin Guardar. */
  readOnly?: boolean;
  /** Nombre de la lista que hoy aplica en la tienda online (otra distinta a esta). */
  currentOnlineListName?: string | null;
  onClose: () => void;
  onSave: (input: PriceListUpsertInput) => Promise<void>;
};

function mapInitialItems(initial: PriceListWithItems | null): DraftItem[] {
  if (!initial?.items?.length) return [];
  return initial.items.map((item) => ({
    id: item.id,
    productId: item.productId,
    productName: '',
    price: item.price != null ? String(item.price) : '',
    percentOff: item.percentOff != null ? String(item.percentOff) : '',
    minQty: item.minQty != null ? String(item.minQty) : '',
  }));
}

export function PriceListFormModal({ visible, initial, readOnly = false, currentOnlineListName = null, onClose, onSave }: Props) {
  const branchId = useSessionStore((s) => s.currentBranchId);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [appliesOnline, setAppliesOnline] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [pricingMode, setPricingMode] = useState<PriceListPricingMode>('fixed');
  const [items, setItems] = useState<DraftItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [onlineSwapConfirmOpen, setOnlineSwapConfirmOpen] = useState(false);
  /** Aviso "deja de aplicar en tu tienda": apagar el flag o desactivar la lista online. */
  const [onlineOffConfirm, setOnlineOffConfirm] = useState<'toggle' | 'deactivate' | null>(null);

  useEffect(() => {
    if (!visible) return;
    setName(initial?.name ?? '');
    setCode(initial?.code ?? '');
    setIsDefault(initial?.isDefault ?? false);
    setAppliesOnline(initial?.appliesOnline ?? false);
    setIsActive(initial?.isActive ?? true);
    setPricingMode(initial?.pricingMode ?? 'fixed');
    setItems(mapInitialItems(initial));
    setProductSearch('');
    setDiscardConfirmOpen(false);
    setOnlineSwapConfirmOpen(false);
    if (!initial?.items?.length) {
      setItemsLoading(false);
      return;
    }
    // Resolver nombres antes de mostrar las filas — nunca exponer productId como nombre.
    setItemsLoading(true);
    let cancelled = false;
    void (async () => {
      const resolved = await Promise.all(
        initial.items.map(async (item) => {
          const product = await getProductById(item.productId).catch(() => null);
          return {
            id: item.id,
            productId: item.productId,
            productName: product?.name ?? 'Producto',
            price: item.price != null ? String(item.price) : '',
            percentOff: item.percentOff != null ? String(item.percentOff) : '',
            minQty: item.minQty != null ? String(item.minQty) : '',
          };
        }),
      );
      if (!cancelled) {
        setItems(resolved);
        setItemsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, initial]);

  const isPercentMode = pricingMode === 'percent_off_base';

  const dirty = useMemo(() => {
    if (!visible) return false;
    if (readOnly) return false;
    if (name.trim() !== (initial?.name ?? '')) return true;
    if (code.trim() !== (initial?.code ?? '')) return true;
    if (isDefault !== (initial?.isDefault ?? false)) return true;
    if (appliesOnline !== (initial?.appliesOnline ?? false)) return true;
    if (isActive !== (initial?.isActive ?? true)) return true;
    if (pricingMode !== (initial?.pricingMode ?? 'fixed')) return true;
    const initItems = mapInitialItems(initial);
    if (items.length !== initItems.length) return true;
    return items.some((item, i) => {
      const init = initItems[i];
      return (
        !init ||
        init.productId !== item.productId ||
        init.price !== item.price ||
        init.percentOff !== item.percentOff ||
        init.minQty !== item.minQty
      );
    });
  }, [visible, readOnly, name, code, isDefault, appliesOnline, isActive, pricingMode, items, initial]);

  const toggleAppliesOnline = useCallback(
    (on: boolean) => {
      // Single-winner: si otra lista ya aplica online, el swap necesita confirmación.
      if (on && currentOnlineListName) {
        setOnlineSwapConfirmOpen(true);
        return;
      }
      // Apagar el flag de una lista que hoy está online → aviso antes.
      if (!on && initial?.appliesOnline) {
        setOnlineOffConfirm('toggle');
        return;
      }
      setAppliesOnline(on);
    },
    [currentOnlineListName, initial?.appliesOnline],
  );

  const toggleIsActive = useCallback(
    (on: boolean) => {
      // Desactivar la lista que aplica online → la tienda deja de mostrarla.
      if (!on && appliesOnline) {
        setOnlineOffConfirm('deactivate');
        return;
      }
      setIsActive(on);
    },
    [appliesOnline],
  );

  const tryClose = useCallback(() => {
    if (dirty && !saving) {
      setDiscardConfirmOpen(true);
      return;
    }
    onClose();
  }, [dirty, saving, onClose]);

  const addProduct = useCallback((row: { id: string; name: string }) => {
    setItems((current) => {
      if (current.some((item) => item.productId === row.id)) return current;
      return [
        ...current,
        { productId: row.id, productName: row.name, price: '', percentOff: '', minQty: '' },
      ];
    });
    setProductSearch('');
  }, []);

  const removeItem = useCallback((productId: string) => {
    setItems((current) => current.filter((item) => item.productId !== productId));
  }, []);

  const updateItemField = useCallback(
    (productId: string, field: 'price' | 'percentOff' | 'minQty', value: string) => {
      setItems((current) =>
        current.map((item) => (item.productId === productId ? { ...item, [field]: value } : item)),
      );
    },
    [],
  );

  const title = readOnly ? 'Detalle de lista' : initial ? 'Editar lista' : 'Nueva lista';
  const subtitle = isPercentMode
    ? 'Cada producto lleva un % de descuento sobre el precio base.'
    : 'Precio fijo por producto, distinto del precio base.';

  const footer = readOnly ? null : (
    <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 12 }}>
      <TicketButton label="Cancelar" variant="ghost" onPress={tryClose} testID="price-lists.form.cancel" />
      <TicketButton
        label={saving ? 'Guardando…' : 'Guardar'}
        variant="gold"
        disabled={saving || !name.trim()}
        testID="price-lists.form.save"
        onPress={async () => {
          if (!name.trim()) return;
          setSaving(true);
          try {
            await onSave({
              name: name.trim(),
              code: code.trim() || null,
              isDefault,
              appliesOnline,
              isActive,
              pricingMode,
              items: items.map((item) => ({
                id: item.id,
                productId: item.productId,
                price: isPercentMode || item.price.trim() === ''
                  ? null
                  : Number(item.price.replace(',', '.')),
                percentOff: !isPercentMode || item.percentOff.trim() === ''
                  ? null
                  : Number(item.percentOff.replace(',', '.')),
                minQty: item.minQty.trim() === '' ? null : Number(item.minQty.replace(',', '.')),
              })),
            });
          } finally {
            setSaving(false);
          }
        }}
      />
    </View>
  );

  return (
    <ModalShell
      visible={visible}
      onClose={tryClose}
      appearance="ticket"
      kicker="Precios"
      title={title}
      subtitle={subtitle}
      footer={footer}
    >
      <View style={{ gap: 4 }}>
        <ModalFormSection title="Identidad">
          <ModalFormField label="Nombre" required>
            <TextInput
              style={ticketControls.input}
              value={name}
              onChangeText={setName}
              placeholder="Ej: Mayorista, Restaurantes"
              placeholderTextColor={ticketColors.ink3}
              editable={!readOnly && !saving}
              testID="price-lists.form.name"
            />
          </ModalFormField>
          <ModalFormField label="Código (opcional)">
            <TextInput
              style={ticketControls.input}
              value={code}
              onChangeText={setCode}
              placeholder="Ej: MAY, RESTO"
              placeholderTextColor={ticketColors.ink3}
              editable={!readOnly && !saving}
              testID="price-lists.form.code"
            />
          </ModalFormField>
        </ModalFormSection>

        <ModalFormSection title="Configuración" divided>
          <View style={switchRow}>
            <Text style={switchLabel}>Lista default</Text>
            <Switch
              value={isDefault}
              onValueChange={setIsDefault}
              disabled={readOnly || saving}
              trackColor={{ true: ticketColors.gold }}
              testID="price-lists.form.default"
            />
          </View>
          <View style={switchRow}>
            <View style={{ flex: 1 }}>
              <Text style={switchLabel}>Mostrar en tienda online</Text>
              <Text style={switchHint}>
                La tienda muestra estos precios cuando el comprador llega a la cantidad mínima.
              </Text>
              {!appliesOnline && currentOnlineListName ? (
                <Text style={switchHint}>{`Hoy tu tienda muestra “${currentOnlineListName}”.`}</Text>
              ) : null}
            </View>
            <Switch
              value={appliesOnline}
              onValueChange={toggleAppliesOnline}
              disabled={readOnly || saving}
              trackColor={{ true: ticketColors.gold }}
              testID="price-lists.form.applies-online"
            />
          </View>
          <View style={switchRow}>
            <Text style={switchLabel}>Activa</Text>
            <Switch
              value={isActive}
              onValueChange={toggleIsActive}
              disabled={readOnly || saving}
              trackColor={{ true: ticketColors.gold }}
              testID="price-lists.form.active"
            />
          </View>
          <View style={switchRow}>
            <View style={{ flex: 1 }}>
              <Text style={switchLabel}>% sobre precio base</Text>
              <Text style={switchHint}>Descuento porcentual por producto en vez de precio fijo.</Text>
            </View>
            <Switch
              value={isPercentMode}
              onValueChange={(on) => setPricingMode(on ? 'percent_off_base' : 'fixed')}
              disabled={readOnly || saving}
              trackColor={{ true: ticketColors.gold }}
              testID="price-lists.form.percent-mode"
            />
          </View>
        </ModalFormSection>

        <ModalFormSection
          title="Productos y precios"
          helper={
            isPercentMode
              ? 'Cargá el % de descuento sobre el precio base. Cant. mín. opcional.'
              : 'Cargá el precio especial por producto. Cant. mín. opcional.'
          }
          divided
        >
          {!readOnly ? (
            <CatalogStockProductSearchFields
              branchId={branchId}
              value={productSearch}
              onChangeText={setProductSearch}
              placeholder="Buscar producto…"
              searchProductsFn={searchProducts}
              onPickSuggestion={(row) => addProduct({ id: row.id, name: row.name })}
            />
          ) : null}

          {itemsLoading ? (
            <Text style={ticketControls.hint}>Cargando productos…</Text>
          ) : items.length === 0 ? (
            <Text style={ticketControls.hint}>Agregá productos con precio especial para esta lista.</Text>
          ) : (
            items.map((item) => (
              <View key={item.productId} style={itemCard}>
                <View style={itemHeader}>
                  <Text style={itemName} numberOfLines={2}>
                    {item.productName}
                  </Text>
                  {!readOnly ? (
                    <TicketButton
                      label="Quitar"
                      variant="danger"
                      onPress={() => removeItem(item.productId)}
                      accessibilityLabel={`Quitar ${item.productName}`}
                      testID={`price-lists.form.item.remove.${item.productId}`}
                    />
                  ) : null}
                </View>
                <View style={itemFields}>
                  <ModalFormField label={isPercentMode ? '% dto.' : 'Precio'} style={{ flex: 1 }}>
                    <NumericTextInput
                      kind={isPercentMode ? 'percent' : 'decimalMoney'}
                      style={ticketControls.input}
                      value={isPercentMode ? item.percentOff : item.price}
                      onChangeText={(value) =>
                        updateItemField(item.productId, isPercentMode ? 'percentOff' : 'price', value)
                      }
                      placeholder={isPercentMode ? 'Ej: 10' : '0,00'}
                      placeholderTextColor={ticketColors.ink3}
                      editable={!readOnly && !saving}
                      testID={`price-lists.form.item.${isPercentMode ? 'percent' : 'price'}.${item.productId}`}
                    />
                  </ModalFormField>
                  <ModalFormField label="Cant. mín." style={{ flex: 1 }}>
                    <NumericTextInput
                      kind="integer"
                      style={ticketControls.input}
                      value={item.minQty}
                      onChangeText={(value) => updateItemField(item.productId, 'minQty', value)}
                      placeholder="Opcional"
                      placeholderTextColor={ticketColors.ink3}
                      editable={!readOnly && !saving}
                      testID={`price-lists.form.item.minqty.${item.productId}`}
                    />
                  </ModalFormField>
                </View>
              </View>
            ))
          )}
        </ModalFormSection>
      </View>

      <OperationalConfirmModal
        visible={discardConfirmOpen}
        title="Descartar cambios"
        message="Tenés cambios sin guardar en esta lista."
        confirmLabel="Descartar"
        destructive
        onClose={() => setDiscardConfirmOpen(false)}
        onConfirm={() => {
          setDiscardConfirmOpen(false);
          onClose();
        }}
      />
      <OperationalConfirmModal
        visible={onlineSwapConfirmOpen}
        title="Cambiar lista online"
        message={`Tu tienda ya muestra “${currentOnlineListName ?? ''}”. Al prender esta, aquella deja de aplicar en la web.`}
        confirmLabel="Usar esta lista"
        onClose={() => setOnlineSwapConfirmOpen(false)}
        onConfirm={() => {
          setOnlineSwapConfirmOpen(false);
          setAppliesOnline(true);
        }}
      />
      <OperationalConfirmModal
        visible={onlineOffConfirm !== null}
        title="Dejar de mostrar en la tienda"
        message={
          onlineOffConfirm === 'deactivate'
            ? 'Si la desactivás, tu tienda online deja de mostrar estos precios por mayor.'
            : 'Tu tienda online deja de mostrar estos precios por mayor.'
        }
        confirmLabel="Dejar de mostrar"
        onClose={() => setOnlineOffConfirm(null)}
        onConfirm={() => {
          const what = onlineOffConfirm;
          setOnlineOffConfirm(null);
          if (what === 'deactivate') setIsActive(false);
          else setAppliesOnline(false);
        }}
      />
    </ModalShell>
  );
}

const switchRow = {
  flexDirection: 'row' as const,
  alignItems: 'center' as const,
  justifyContent: 'space-between' as const,
  paddingVertical: 6,
  gap: 12,
};

const switchLabel = {
  fontSize: 14,
  fontWeight: '600' as const,
  color: ticketColors.ink,
};

const switchHint = {
  fontSize: 11,
  color: ticketColors.ink3,
  marginTop: 2,
};

const itemCard = {
  borderWidth: 1,
  borderColor: ticketColors.ruleSoft,
  borderRadius: 10,
  padding: 10,
  gap: 8,
  backgroundColor: ticketColors.paper,
};

const itemHeader = {
  flexDirection: 'row' as const,
  alignItems: 'center' as const,
  justifyContent: 'space-between' as const,
  gap: 8,
};

const itemName = {
  flex: 1,
  fontSize: 14,
  fontWeight: '600' as const,
  color: ticketColors.ink,
};

const itemFields = {
  flexDirection: 'row' as const,
  gap: 12,
};
